import type {
  ConnectorPlatform,
  ConnectorStatus,
  MediaConnector,
  SyncCadence,
} from '../model/types';
import { mediaHttp } from '@/lib/api/http-client';
import { parseResponse } from '@/lib/api/parse-response';
import { mediaConnectorSchema, oauthInitiateSchema } from './schemas';

export interface TriggerSyncInput {
  connectorId: string;
  projectId?: string;
  forceFullSync?: boolean;
}

export async function triggerSync(input: TriggerSyncInput): Promise<void> {
  await mediaHttp.post({
    responseType: 'json',
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/api/v1/media-sync/{connectorId}/trigger',
    path: { connectorId: input.connectorId },
    body: { projectId: input.projectId, forceFullSync: input.forceFullSync },
  });
}

export async function initiateOAuth(
  platform: ConnectorPlatform,
  title: string,
  tags: string[],
  syncCadence?: SyncCadence,
  advertiserId?: string,
  accountId?: string,
): Promise<string> {
  const res = await mediaHttp.post({
    responseType: 'json',
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/api/v1/media-connectors/oauth/{platform}/initiate',
    path: { platform },
    body: { title, tags, syncCadence, advertiserId, accountId },
  });

  return parseResponse(oauthInitiateSchema, res).authUrl;
}

export async function disconnectConnector(connectorId: string): Promise<void> {
  await mediaHttp.delete({
    responseType: 'json',
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/api/v1/media-connectors/{connectorId}',
    path: { connectorId },
  });
}

export async function refreshConnector(connectorId: string): Promise<void> {
  await mediaHttp.post({
    responseType: 'json',
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/api/v1/media-connectors/{connectorId}/refresh',
    path: { connectorId },
  });
}

/**
 * Probes each capability now and returns the connector with the answers recorded.
 *
 * The point of a button: health is otherwise a by-product of scheduled work, so a
 * connector nobody has used yet has no answers at all, and someone who has just enabled an
 * API has no way to see it worked without waiting for the next sync.
 */
export async function testConnector(connectorId: string): Promise<MediaConnector> {
  const res = await mediaHttp.post({
    responseType: 'json',
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/api/v1/media-connectors/{connectorId}/test',
    path: { connectorId },
  });
  return parseResponse(mediaConnectorSchema, res);
}

export interface UpdateConnectorInput {
  title?: string;
  tags?: string[];
  status?: ConnectorStatus;
  syncCadence?: SyncCadence;
  dsaBeneficiary?: string | null;
  dsaPayor?: string | null;
  advertiserId?: string | null;
  advertiserName?: string | null;
}

export async function updateConnector(
  connectorId: string,
  input: UpdateConnectorInput,
): Promise<MediaConnector> {
  const res = await mediaHttp.patch({
    responseType: 'json',
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/api/v1/media-connectors/{connectorId}',
    path: { connectorId },
    body: input,
  });

  return parseResponse(mediaConnectorSchema, res);
}

export async function connectWithToken(
  platform: ConnectorPlatform,
  title: string,
  tags: string[],
  token: string,
  syncCadence?: SyncCadence,
  /** Where the platform nests advertisers and the account id is not one. */
  advertiserId?: string,
): Promise<MediaConnector> {
  const res = await mediaHttp.post({
    responseType: 'json',
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/api/v1/media-connectors/connect/{platform}/token',
    path: { platform },
    body: { title, tags, token, syncCadence, advertiserId },
  });

  return parseResponse(mediaConnectorSchema, res);
}

export async function connectWithServiceAccount(
  platform: ConnectorPlatform,
  title: string,
  tags: string[],
  keyFileContent: string,
  syncCadence?: SyncCadence,
  /** Google Ads only: the manager account to operate as, where one holds the account. */
  loginCustomerId?: string,
  /** Where the platform nests advertisers and the account id is not one. */
  advertiserId?: string,
): Promise<MediaConnector> {
  const res = await mediaHttp.post({
    responseType: 'json',
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/api/v1/media-connectors/connect/{platform}/service-account',
    path: { platform },
    body: { title, tags, keyFileContent, syncCadence, loginCustomerId, advertiserId },
  });

  return parseResponse(mediaConnectorSchema, res);
}
